// Thin client for the Mazu API. The session lives in an httpOnly cookie
// set at sign-in, so nothing secret is held in JavaScript.

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

export async function api<T = any>(path: string, init: { method?: string; body?: unknown; form?: FormData } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? (init.body !== undefined || init.form ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: init.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: init.form ?? (init.body !== undefined ? JSON.stringify(init.body) : undefined),
  });
  const text = await res.text();
  const data = text && res.headers.get('content-type')?.includes('json') ? JSON.parse(text) : text;
  if (!res.ok) {
    if (res.status === 401 && path !== '/v1/auth/login') window.dispatchEvent(new Event('ll:signed-out'));
    throw new ApiError(res.status, data?.error ?? 'error', data?.message ?? `Request failed (${res.status})`, data?.details);
  }
  return data as T;
}

export const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function propose(schema: string, payload: unknown, rationale?: string) {
  return api<Outcome>('/v1/proposals', { body: { schema, payload, rationale, idempotency_key: newKey() } });
}

export interface Me {
  user_id: string; email: string; display_name: string; role: 'owner' | 'admin' | 'manager' | 'member'; mfa_enabled: boolean; mfa_verified: boolean;
  tenant_id: string; tenant_slug: string; tenant_name: string;
  /** Signed in with a temporary password: a new one comes before anything else. */
  password_change_required?: boolean;
  /** This server holds you to two-factor sign-in: without a verified session, enrolling it comes next. */
  mfa_required?: boolean;
  /** The owner's welcome interview: offered, started, deferred, cancelled, or done; null when it was never offered. */
  onboarding?: 'offered' | 'started' | 'deferred' | 'cancelled' | 'done' | null;
  /** Features this workspace's plan includes; the screens of the rest are hidden. */
  features?: string[];
  /** The pieces of the app this workspace's hired agents bring; the rest are hidden. */
  surfaces?: string[];
  /** What this person may do (verb:domain); a screen whose read scope is missing is hidden. */
  scopes?: string[];
  /** The workspace's own role this person holds, or null for the built-in one their role names. */
  role_key?: string | null; role_name?: string | null;
}

export interface Reason { code: string; message: string; rule_id?: string; details?: unknown }
export interface Outcome {
  id: string;
  state: string;
  reasons: Reason[];
  route?: { approval_id: string; assignee_user_id: string | null; assignee_role: string | null; signoffs_required: number };
  would?: string;
  result?: Record<string, any>;
  computed?: Record<string, any>;
}

export interface PartySummary { id: string; kind: 'person' | 'household' | 'organization'; display_name: string; status: string; review_state: string; grade: string | null; tags: string[]; owner_user_id: string | null; version: number; email: string | null; phone: string | null }

export interface Party extends Omit<PartySummary, 'email' | 'phone'> {
  given_name: string | null; family_name: string | null; org_name: string | null; parent_party_id: string | null;
  merged_into_id: string | null; visibility: string; contact_class: string; lifecycle_stage: string | null; custom: Record<string, unknown>; source: Record<string, any>;
  external_ids: Record<string, string[]>; created_at: string; updated_at: string;
  contact_points: Array<{ id: string; kind: 'email' | 'phone' | 'address'; label: string; value: any; normalized: string | null; is_primary: boolean }>;
  relationships: Array<{ id: string; kind: string; role: string | null; direction: 'in' | 'out'; other_name: string; other_party_id: string }>;
  occasions: Array<{ id: string; kind: string; on_date: string; label: string | null }>;
  notes: Array<{ id: string; body: string; author: string; created_at: string }>;
  history: Array<{ seq: string; version: number | null; op: string; proposal_id: string; at: string }>;
}

export interface Candidate { party_id: string; display_name: string; score: number; level: 'duplicate' | 'possible' | 'none'; reasons: string[] }
